<template>
  <div class="emergency-info" v-if="loadedData">
    <!-- Header Section -->
    <div class="info-header">
      <div class="header-content">
        <div
          @click="toggleChildModal"
          class="emergency-badge"
          :class="getEmergencyTypeClass(loadedData.emergency.emergencyType)"
        >
          <i :class="getEmergencyIcon(loadedData.emergency.emergencyType)"></i>
          <span>{{ formatEmergencyType(loadedData.emergency.emergencyType) }}</span>
          <i class="bi bi-pencil"></i>
        </div>
        <div class="header-actions">
          <div
            class="priority-badge"
            :class="getPriorityClass(loadedData.emergency.score)"
          >
            Fraud Probability: {{ loadedData.emergency.score }}%
          </div>
          <div class="status-badge" :class="getStatusClass(loadedData.emergency.status)">
            {{ loadedData.emergency.status }}
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content Grid -->
    <div class="content-grid">
      <!-- Left Column - Emergency Details -->
      <div class="details-section">
        <!-- Caller Information -->
        <div class="info-card">
          <div class="card-header">
            <i class="bi bi-person-circle"></i>
            <h3>Caller Information</h3>
          </div>
          <div class="card-body">
            <div class="info-row">
              <div class="info-label">Caller Name</div>
              <div class="info-value">
                {{ loadedData.emergency.callerName || "Unknown" }}
              </div>
            </div>
            <div class="info-row">
              <div class="info-label">User ID</div>
              <div class="info-value code">{{ loadedData.emergency.userId }}</div>
            </div>
          </div>
        </div>

        <!-- Emergency Details -->
        <div class="info-card">
          <div class="card-header">
            <i class="bi bi-info-circle"></i>
            <h3>Emergency Details</h3>
          </div>
          <div class="card-body">
            <div class="info-row">
              <div class="info-label">Request ID</div>
              <div class="info-value code">{{ loadedData.emergency.eRId }}</div>
            </div>
            <div class="info-row">
              <div class="info-label">Call ID</div>
              <div class="info-value code">{{ loadedData.emergency.eCId }}</div>
            </div>
            <div class="info-row">
              <div class="info-label">Created At</div>
              <div class="info-value highlight">
                {{ loadedData.emergency.createdAtString }}
              </div>
            </div>
            <div class="info-row">
              <div class="info-label">Call Status</div>
              <div class="info-value">
                <span
                  class="status-tag"
                  :class="getCallStatusClass(loadedData.emergency.callStatus)"
                >
                  {{ loadedData.emergency.callStatus }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Location Information -->
        <div class="info-card">
          <div class="card-header">
            <i class="bi bi-geo-alt"></i>
            <h3>Location Information</h3>
          </div>
          <div class="card-body">
            <div class="info-row">
              <div class="info-label">Location</div>
              <div class="info-value">
                {{ loadedData.emergency.location || "Unknown location" }}
              </div>
            </div>
            <div class="coordinates-grid">
              <div class="coordinate-item">
                <div class="coord-label">Latitude</div>
                <div class="coord-value">{{ loadedData.emergency.latitude }}</div>
              </div>
              <div class="coordinate-item">
                <div class="coord-label">Longitude</div>
                <div class="coord-value">{{ loadedData.emergency.longitude }}</div>
              </div>
            </div>
            <button @click="toggleMapModal" class="view-map-btn">
              <i class="bi bi-map"></i>
              View on Map
            </button>
          </div>
        </div>

        <!-- Additional Information -->
        <div class="info-card">
          <div class="card-header">
            <i class="bi bi-chat-text"></i>
            <h3>Additional Information</h3>
          </div>
          <div class="card-body">
            <div class="d-flex justify-content-end">
              <button
                @click="toggleNotesModal"
                type="button"
                class="btn btn-sm btn-success"
              >
                +
              </button>
            </div>
            <div class="info-row" v-if="loadedData.emergency.remarks">
              <div class="info-label">Remarks</div>
              <div class="info-value remarks">{{ loadedData.emergency.remarks }}</div>
            </div>
            <div class="info-row" v-if="loadedData.emergency.warnings">
              <div class="info-label">Warnings</div>
              <div class="info-value warnings">{{ loadedData.emergency.warnings }}</div>
            </div>
            <div
              class="info-row"
              v-if="!loadedData.emergency.remarks && !loadedData.emergency.warnings"
            >
              <div class="no-data">No additional information available</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column - Activity Log -->
      <div class="logs-section">
        <div class="info-card">
          <div class="card-header">
            <i class="bi bi-clock-history"></i>
            <h3>Activity Log</h3>
            <span class="log-count">{{ loadedData.logs.length }} entries</span>
          </div>
          <div class="card-body logs-body">
            <div class="timeline">
              <div v-for="log in loadedData.logs" :key="log.logId" class="timeline-item">
                <div class="timeline-marker" :class="getLogTypeClass(log.logType)"></div>
                <div class="timeline-content">
                  <div class="log-header">
                    <span class="log-type" :class="getLogTypeClass(log.logType)">{{
                      log.logType
                    }}</span>
                    <span class="log-time">{{ formatLogTime(log.createdAt) }}</span>
                  </div>
                  <div class="log-details" v-if="log.details">{{ log.details }}</div>
                  <div class="log-user" v-if="log.userFullName">
                    <i class="bi bi-person"></i>
                    {{ log.userFullName }}
                  </div>
                </div>
              </div>
              <div v-if="loadedData.logs.length === 0" class="no-logs">
                <i class="bi bi-inbox"></i>
                <p>No activity logs available</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Responder Information -->
        <div class="info-card">
          <div class="card-header">
            <i class="bi bi-person-badge"></i>
            <h3>Assigned Personnel</h3>
          </div>
          <div class="card-body">
            <div class="info-row">
              <div class="info-label">Operator</div>
              <div class="info-value">
                {{ loadedData.emergency.operatorName || "Unassigned" }}
              </div>
            </div>
            <div class="info-row">
              <div class="info-label">Operator ID</div>
              <div class="info-value code">{{ loadedData.emergency.operatorId }}</div>
            </div>
            <div class="info-row">
              <div class="info-label">Responder ID</div>
              <div class="info-value code">{{ loadedData.emergency.responderId }}</div>
            </div>
          </div>
        </div>

        <!-- Dispatch Information -->
        <div class="info-card">
          <div class="card-header">
            <i class="bi bi-person-badge"></i>
            <h3>Dispatched Teams/Agencies</h3>
            <button
              @click="toggleDispatchModal"
              style="margin-left: auto"
              class="btn btn-sm btn-success"
            >
              +
            </button>
          </div>
          <div class="card-body">
            <div
              v-for="responder in loadedData.responders"
              :key="responder.disptachId"
              class="info-row"
            >
              <div class="info-label">{{ responder.responderName }}</div>
              <div class="info-value">
                {{ responder.agency || "Unassigned" }}
              </div>
            </div>
            <div v-if="loadedData.responders.length === 0" class="no-logs">
              <i class="bi bi-inbox"></i>
              <p>No dispatch logs available</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div v-else-if="props.id" class="loading-state">
    <div class="loading-spinner"></div>
    <p>Loading emergency request details...</p>
  </div>

  <!-- Empty State -->
  <div v-else class="empty-state">
    <i class="bi bi-search"></i>
    <h3>No Emergency Selected</h3>
    <p>Select an emergency request from the list to view details</p>
  </div>

  <ChildModal :show="showChildModal" @close="toggleChildModal">
    <EditEmergencyType
      :id="loadedData?.emergency?.erId"
      :emergncy-tpe="loadedData?.emergency?.emergencyType"
      @confirmed="handleConfirmed"
      @closed="toggleChildModal"
    />
  </ChildModal>

  <MapModal
    ref="mapModalRef"
    :show="showMapModal"
    :id="loadedData?.emergency?.erId"
    :caller-coordinates="callerLocation"
    :caller-location="loadedData?.emergency?.location"
    @close="toggleMapModal"
  >
  </MapModal>

  <NotesModal
    :id="loadedData?.emergency?.erId"
    :show="showNotesModal"
    @confirm="onNotesAdded"
    @close="toggleNotesModal"
  />

  <AlertModal
    @close="toggleAlertModal"
    :title="alertTitle"
    :type="alertType"
    :show="showAlert"
    :message="alertMessage"
  />

  <DispatchModal
    ref="dispatchModalRef"
    :show="showDispatch"
    :emergency-id="loadedData?.emergency?.erId"
    :emergency-type="loadedData?.emergency?.emergencyType"
    :caller-location="loadedData?.emergency?.location"
    @dispatch="handleDispatch"
    @close="toggleDispatchModal"
    @error="onDispatchError"
  />
</template>

<script setup>
import emergencyService from "@/services/emergencyService";
import ChildModal from "@/components/modals/ChildModal.vue";
import { ref, watch } from "vue";
import EditEmergencyType from "./EditEmergencyType.vue";
import MapModal from "../modals/MapModal.vue";
import emergencyTypeHelper from "@/utils/emergencyTypeHelper";
import NotesModal from "../modals/NotesModal.vue";
import DispatchModal from "../modals/DispatchModal.vue";
import AlertModal from "../modals/AlertModal.vue";
// eslint-disable-next-line
const props = defineProps({
  id: String,
});

const callerLocation = ref({ lat: 11.047606482674592, lng: 124.38233399033454 });
const showNotesModal = ref(false);
const showChildModal = ref(false);
const showMapModal = ref(false);
const toggleChildModal = () => {
  showChildModal.value = !showChildModal.value;
};
const mapModalRef = ref(null);
const toggleMapModal = () => {
  showMapModal.value = !showMapModal.value;
  if (showMapModal.value) {
    mapModalRef.value.clearResponderMarkers();
  }
};
//Alert Modal
const showAlert = ref(false);
const alertType = ref("error");
const alertMessage = ref("");
const alertTitle = ref("Error");
const showDispatch = ref(false);
const dispatchModalRef = ref(null);
const onDispatchError = (msg) => {
  showAlert.value = true;
  alertTitle.value = "Dispatch Error";
  alertMessage.value = msg;
};

const toggleAlertModal = () => {
  showAlert.value = !showAlert.value;
};

const toggleDispatchModal = () => {
  showDispatch.value = !showDispatch.value;
  if (showDispatch.value) dispatchModalRef.value.getActiveAgencies();
};

const handleDispatch = (dispatchData) => {
  toggleDispatchModal();
  dispatchData;
  emergencyService.getInfo(loadedData.value.emergency.erId);
};
const onNotesAdded = (notes) => {
  toggleNotesModal();
  loadedData.value.emergency.remarks = `${loadedData.value.emergency.remarks} ${notes}`;
};

const toggleNotesModal = () => {
  showNotesModal.value = !showNotesModal.value;
};

const handleConfirmed = (id) => {
  toggleChildModal();
  loadedData.value.emergency.emergencyType = id;
};
const loadedData = ref(null);

const getInfo = async () => {
  if (props.id) {
    try {
      loadedData.value = await emergencyService.getInfo(props.id);
      callerLocation.value = {
        lat: loadedData.value.emergency.latitude,
        lng: loadedData.value.emergency.longitude,
      };
    } catch (error) {
      console.error("Failed to load emergency info:", error);
    }
  }
};

watch(
  () => props.id,
  (newId) => {
    if (newId) getInfo();
    else loadedData.value = null;
  },
  { immediate: true }
);

// Helper methods
const getEmergencyTypeClass = (type) => {
  return emergencyTypeHelper.getEmergencyTypeClass(type);
};

const getEmergencyIcon = (type) => {
  return emergencyTypeHelper.getEmergencyIcon(type);
};

const formatEmergencyType = (type) => {
  return emergencyTypeHelper.formatEmergencyType(type);
};

const getPriorityClass = (score) => {
  if (score >= 50) return "critical";
  if (score >= 25) return "high";
  if (score >= 15) return "medium";
  return "low";
};

const getStatusClass = (status) => {
  const statusMap = {
    pending: "pending",
    "in-progress": "in-progress",
    completed: "completed",
    cancelled: "cancelled",
    dispatched: "dispatched",
    close: "closed",
  };
  return statusMap[status?.toLowerCase()] || "unknown";
};

const getCallStatusClass = (callStatus) => {
  const statusMap = {
    connected: "success",
    disconnected: "error",
    ringing: "warning",
    failed: "error",
  };
  return statusMap[callStatus.toString()?.toLowerCase()] || "unknown";
};

const getLogTypeClass = (logType) => {
  const typeMap = {
    created: "created",
    updated: "updated",
    dispatched: "dispatched",
    completed: "completed",
    cancelled: "cancelled",
  };
  return typeMap[logType?.toLowerCase()] || "default";
};

const formatLogTime = (dateTime) => {
  return new Date(dateTime).toLocaleString();
};
</script>

<style scoped>
.emergency-info {
  animation: fadeIn 0.5s ease-in;
}

/* Header Styles */
.info-header {
  background: linear-gradient(135deg, #a30e0e 0%, #8a0b0b 100%);
  color: white;
  padding: 1.5rem 2rem;
  border-radius: 16px 16px 0 0;
  margin-bottom: 1.5rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.emergency-badge {
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  font-weight: 600;
  font-size: 1.1rem;
}

.emergency-badge i {
  font-size: 1.5rem;
}

.header-actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.priority-badge,
.status-badge {
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.priority-badge.critical {
  background: #dc2626;
  color: white;
}
.priority-badge.high {
  background: #ea580c;
  color: white;
}
.priority-badge.medium {
  background: #d97706;
  color: white;
}
.priority-badge.low {
  background: #65a30d;
  color: white;
}

.status-badge.pending {
  background: #f59e0b;
  color: white;
}
.status-badge.in-progress {
  background: #3b82f6;
  color: white;
}
.status-badge.completed {
  background: #10b981;
  color: white;
}
.status-badge.cancelled {
  background: #ef4444;
  color: white;
}
.status-badge.dispatched {
  background: #8b5cf6;
  color: white;
}
.status-badge.unknown {
  background: #6b7280;
  color: white;
}

/* Content Grid */
.content-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  padding: 0 2rem 2rem;
}

@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
}

/* Info Cards */
.info-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.info-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.card-header {
  background: #f8fafc;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.card-header h3 {
  margin: 0;
  color: #1f2937;
  font-size: 1.1rem;
  font-weight: 600;
}

.card-header i {
  color: #a30e0e;
  font-size: 1.2rem;
}

.log-count {
  margin-left: auto;
  background: #e5e7eb;
  color: #6b7280;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.card-body {
  padding: 1.5rem;
}

/* Info Rows */
.info-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f3f4f6;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  color: #6b7280;
  font-weight: 500;
  font-size: 0.9rem;
  min-width: 120px;
}

.info-value {
  color: #1f2937;
  font-weight: 500;
  text-align: right;
  flex: 1;
  margin-left: 1rem;
}

.info-value.code {
  font-family: "Courier New", monospace;
  font-size: 0.85rem;
  color: #6b7280;
  word-break: break-all;
}

.info-value.highlight {
  color: #a30e0e;
  font-weight: 600;
}

.info-value.remarks,
.info-value.warnings {
  background: #fef3c7;
  padding: 0.5rem;
  border-radius: 6px;
  border-left: 3px solid #f59e0b;
  text-align: left;
}

.info-value.warnings {
  background: #fee2e2;
  border-left-color: #dc2626;
}

/* Coordinates Grid */
.coordinates-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin: 1rem 0;
}

.coordinate-item {
  background: #f8fafc;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.coord-label {
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.coord-value {
  font-family: "Courier New", monospace;
  font-weight: 600;
  color: #1f2937;
}

/* Buttons */
.view-map-btn {
  width: 100%;
  background: linear-gradient(135deg, #a30e0e, #8a0b0b);
  color: white;
  border: none;
  padding: 0.75rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.view-map-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(163, 14, 14, 0.3);
}

/* Status Tags */
.status-tag {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: capitalize;
}

.status-tag.success {
  background: #dcfce7;
  color: #166534;
}
.status-tag.error {
  background: #fee2e2;
  color: #dc2626;
}
.status-tag.warning {
  background: #fef3c7;
  color: #d97706;
}
.status-tag.unknown {
  background: #f3f4f6;
  color: #6b7280;
}

/* Timeline for Activity Logs */
.logs-body {
  max-height: 400px;
  overflow-y: auto;
}

.timeline {
  position: relative;
}

.timeline::before {
  content: "";
  position: absolute;
  left: 15px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e5e7eb;
}

.timeline-item {
  display: flex;
  margin-bottom: 1.5rem;
  position: relative;
}

.timeline-marker {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #6b7280;
  margin-right: 1rem;
  margin-top: 0.25rem;
  flex-shrink: 0;
  position: relative;
  z-index: 2;
}

.timeline-marker.created {
  background: #10b981;
}
.timeline-marker.updated {
  background: #3b82f6;
}
.timeline-marker.dispatched {
  background: #8b5cf6;
}
.timeline-marker.completed {
  background: #f59e0b;
}
.timeline-marker.cancelled {
  background: #ef4444;
}

.timeline-content {
  flex: 1;
  background: #f8fafc;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.log-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.log-type {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: capitalize;
}

.log-type.created {
  background: #dcfce7;
  color: #166534;
}
.log-type.updated {
  background: #dbeafe;
  color: #1e40af;
}
.log-type.dispatched {
  background: #ede9fe;
  color: #6d28d9;
}
.log-type.completed {
  background: #fef3c7;
  color: #d97706;
}
.log-type.cancelled {
  background: #fee2e2;
  color: #dc2626;
}

.log-time {
  font-size: 0.8rem;
  color: #6b7280;
}

.log-details {
  color: #4b5563;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

.log-user {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #6b7280;
}

.no-logs {
  text-align: center;
  padding: 2rem;
  color: #9ca3af;
}

.no-logs i {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

/* Loading and Empty States */
.loading-state,
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #6b7280;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #a30e0e;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1.5rem;
}

.empty-state i {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state h3 {
  margin: 0 0 0.5rem 0;
  color: #4b5563;
}

.empty-state p {
  margin: 0;
  font-size: 0.9rem;
}

.no-data {
  color: #9ca3af;
  font-style: italic;
  text-align: center;
  padding: 1rem;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .info-header {
    padding: 1rem;
  }

  .header-content {
    flex-direction: column;
    align-items: stretch;
  }

  .emergency-badge {
    justify-content: center;
  }

  .header-actions {
    justify-content: center;
  }

  .content-grid {
    padding: 0 1rem 1rem;
    gap: 1rem;
  }

  .info-row {
    flex-direction: column;
    gap: 0.5rem;
  }

  .info-label {
    min-width: auto;
  }

  .info-value {
    text-align: left;
    margin-left: 0;
  }

  .coordinates-grid {
    grid-template-columns: 1fr;
  }

  .log-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}
</style>
